import { Button, Checkbox, ModalPanel, Title, Text } from "dash-styled"
import React, { FC, useCallback, useMemo, useState } from "react"
import {
    APIJobsPerStatusModel,
    MappedJobStatus,
    DataPointsToUpdate
} from "../../../../../../Models/API/APIJobsPerStatusModel"
import { Banner } from "../../../../../Common/Banner/Banner"
interface UpdateDataPointsModalProps {
    isSaving: boolean
    jobCount: APIJobsPerStatusModel | undefined
    handleSave: (checkboxes: DataPointsToUpdate) => void
    handleClose: () => void
}

export const UpdateDataPointsModal: FC<UpdateDataPointsModalProps> = ({
    isSaving,
    jobCount,
    handleSave,
    handleClose
}) => {
    const dataPointsWillBeRelabeledHint =
        "These data points will be reprocessed"

    const dataPointsCount = useMemo(() => {
        return {
            [MappedJobStatus.unprocessed]: jobCount
                ? APIJobsPerStatusModel.unprocessed(jobCount)
                : 0,
            [MappedJobStatus.inProgress]: jobCount?.IN_PROGRESS || 0,
            [MappedJobStatus.completed]: jobCount?.COMPLETED || 0
        }
    }, [jobCount])

    const [checkboxes, setCheckboxes] = useState<DataPointsToUpdate>({
        [MappedJobStatus.unprocessed]: true,
        [MappedJobStatus.inProgress]: false,
        [MappedJobStatus.completed]: false
    })

    const handleChange = useCallback(
        (key: MappedJobStatus) => {
            setCheckboxes({ ...checkboxes, [key]: !checkboxes[key] })
        },
        [checkboxes]
    )

    return (
        <ModalPanel
            testId="update-data-points-modal"
            onClose={handleClose}
            className="max-w-2xl w-full"
        >
            <Title variant="heading2" weight="semibold" component="h2">
                Which data points to update?
            </Title>
            <div className="mb-4">
                <Checkbox
                    variant="modern"
                    label={`Unprocessed (${dataPointsCount.UNPROCESSED})`}
                    isChecked={checkboxes.UNPROCESSED}
                    className="text-xs px-1 mb-3"
                    onChange={() => handleChange(MappedJobStatus.unprocessed)}
                />
                <Checkbox
                    variant="modern"
                    label={`In Progress (${dataPointsCount.IN_PROGRESS})`}
                    isChecked={checkboxes.IN_PROGRESS}
                    className={`text-xs px-1 ${
                        checkboxes.IN_PROGRESS ? "mb-0.5" : "mb-3"
                    }`}
                    onChange={() => handleChange(MappedJobStatus.inProgress)}
                />
                {checkboxes.IN_PROGRESS ? (
                    <Text
                        className="ml-8 mt-0 mb-0 text-smoke-810"
                        component="p"
                        weight="regular"
                        color="currentColor"
                        size="sm"
                    >
                        {dataPointsWillBeRelabeledHint}
                    </Text>
                ) : null}
                <Checkbox
                    variant="modern"
                    label={`Completed (${dataPointsCount.COMPLETED})`}
                    isChecked={checkboxes.COMPLETED}
                    className={`text-xs px-1 ${
                        checkboxes.COMPLETED ? "mb-0.5" : "mb-3"
                    }`}
                    onChange={() => handleChange(MappedJobStatus.completed)}
                />
                {checkboxes.COMPLETED ? (
                    <Text
                        className="ml-8 mt-0 mb-0 text-smoke-810"
                        component="p"
                        weight="regular"
                        color="currentColor"
                        size="sm"
                    >
                        {dataPointsWillBeRelabeledHint}
                    </Text>
                ) : null}
                <Checkbox
                    variant="modern"
                    label="Future data points you upload"
                    isChecked={true}
                    disabled={true}
                    className="text-xs px-1"
                />
            </div>
            <Banner icon="info" className="text-sm">
                Number of data points updates every minute
            </Banner>
            <Button
                testId="data-points-update-save-button"
                type="submit"
                className="mt-7 mx-auto w-48"
                disabled={isSaving}
                onClick={() => handleSave(checkboxes)}
            >
                {isSaving ? "Saving..." : "Save changes"}
            </Button>
        </ModalPanel>
    )
}
